<template>
  <section class="section-block">
    <div class="section-heading-row">
      <div>
        <h2><i class="fas fa-users"></i> Agents &amp; parties</h2>
        <p class="section-hint">
          Brokers tied to this entity. Use <strong>Agency contact</strong> for the producer or account manager;
          set a separate <strong>Certificate request destination</strong> when COI requests go to a shared inbox
          (for example certificates@agency.com) for all linked locations in your account.
        </p>
      </div>
      <button type="button" class="btn btn-outline btn-sm" @click="startAdd">
        Add party
      </button>
    </div>

    <form v-if="adding" class="party-form" @submit.prevent="saveNew">
      <label>
        Agency
        <input
          v-model="searchQuery"
          type="search"
          placeholder="Search agencies"
          @input="onSearchInput"
        />
      </label>
      <ul v-if="searchResults.length" class="search-results">
        <li v-for="entity in searchResults" :key="entity.id">
          <button type="button" @click="selectEntity(entity)">{{ entity.name }}</button>
        </li>
      </ul>
      <p v-if="selectedEntity" class="selected-entity">Selected: {{ selectedEntity.name }}</p>
      <fieldset>
        <legend>Coverage lines</legend>
        <label v-for="option in coverageOptions" :key="option.code" class="check-option">
          <input v-model="draft.coverageTypes" type="checkbox" :value="option.code" />
          {{ option.label }}
        </label>
      </fieldset>
      <label>
        Notes
        <input v-model="draft.notes" type="text" maxlength="500" />
      </label>
      <label>
        Agency contact email
        <input v-model="draft.email" type="email" />
      </label>
      <label>
        Phone
        <input v-model="draft.phone" type="tel" />
      </label>
      <p v-if="formError" class="form-error">{{ formError }}</p>
      <div class="form-actions">
        <button type="submit" class="btn btn-primary btn-sm" :disabled="saving">Save party</button>
        <button type="button" class="btn btn-ghost btn-sm" @click="cancelAdd">Cancel</button>
      </div>
    </form>

    <div v-if="agentLinks.length" class="parties-table-wrap">
      <table class="parties-table">
        <thead>
          <tr>
            <th>Agent</th>
            <th>Coverage types</th>
            <th>Contact</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="link in agentLinks" :key="link.id">
            <tr>
              <td>
                <strong>{{ link.agent_entity_name }}</strong>
                <span v-if="link.notes" class="party-note">{{ link.notes }}</span>
              </td>
              <td>
                <span
                  v-for="label in (link.coverage_labels || link.coverage_types || [])"
                  :key="label"
                  class="coverage-type-tag"
                >{{ label }}</span>
                <span v-if="!(link.coverage_labels || link.coverage_types || []).length" class="muted">Not set</span>
              </td>
              <td class="contact-cell">
                <span class="contact-kind">Agency contact</span>
                <span v-if="link.agent_entity_email">{{ link.agent_entity_email }}</span>
                <span v-if="link.agent_entity_phone">{{ link.agent_entity_phone }}</span>
                <span v-if="!link.agent_entity_email && !link.agent_entity_phone" class="muted">No contact</span>
              </td>
              <td class="row-actions">
                <button type="button" class="btn-link" @click="startEdit(link)">Edit</button>
                <button type="button" class="btn-link danger" @click="removeLink(link)">Remove</button>
              </td>
            </tr>
            <tr v-if="routingFeatureOn" class="cert-route-row">
              <td colspan="4">
                <div class="cert-route-panel">
                  <div class="cert-route-heading">
                    <strong>Certificate request destination</strong>
                    <span v-if="routeLoading[link.id]" class="muted">Loading…</span>
                  </div>
                  <template v-if="!routeLoading[link.id] && routeByLinkId[link.id]">
                    <p v-if="routeByLinkId[link.id].route?.status === 'VERIFIED'" class="cert-route-copy">
                      Verified inbox <strong>{{ routeByLinkId[link.id].route.email }}</strong>
                      is used for certificate and gap requests for
                      <strong>{{ routeByLinkId[link.id].affected_subject_count }}</strong>
                      linked insured{{ routeByLinkId[link.id].affected_subject_count === 1 ? '' : 's' }}
                      in this account.
                    </p>
                    <p
                      v-else-if="routeByLinkId[link.id].suggested_email"
                      class="cert-route-copy"
                    >
                      We have seen
                      <strong>{{ routeByLinkId[link.id].suggested_email }}</strong>
                      as a certificate desk for this agency elsewhere. Confirm to use it for all
                      <strong>{{ routeByLinkId[link.id].affected_subject_count }}</strong>
                      linked insured{{ routeByLinkId[link.id].affected_subject_count === 1 ? '' : 's' }}
                      in this account.
                    </p>
                    <p v-else class="cert-route-copy muted">
                      No verified certificate desk yet. Add the shared inbox your agency uses for COI requests.
                    </p>
                    <div class="cert-route-actions">
                      <input
                        v-model="routeDraftEmail[link.id]"
                        type="email"
                        class="cert-route-input"
                        placeholder="certificates@agency.com"
                      />
                      <button
                        type="button"
                        class="btn btn-primary btn-sm"
                        :disabled="routeSaving[link.id]"
                        @click="confirmRoute(link)"
                      >
                        Confirm for this account
                      </button>
                      <button
                        v-if="routeByLinkId[link.id].suggested_email"
                        type="button"
                        class="btn btn-ghost btn-sm"
                        :disabled="routeSaving[link.id]"
                        @click="rejectSuggestion(link)"
                      >
                        Not for this account
                      </button>
                      <button
                        v-if="routeByLinkId[link.id].route"
                        type="button"
                        class="btn btn-ghost btn-sm danger-text"
                        :disabled="routeSaving[link.id]"
                        @click="clearRoute(link)"
                      >
                        Clear
                      </button>
                    </div>
                    <p v-if="routeErrors[link.id]" class="form-error">{{ routeErrors[link.id] }}</p>
                  </template>
                </div>
              </td>
            </tr>
            <tr v-if="editingId === link.id">
              <td colspan="4">
                <form class="party-form inline" @submit.prevent="saveEdit(link)">
                  <fieldset>
                    <legend>Coverage lines</legend>
                    <label v-for="option in coverageOptions" :key="option.code" class="check-option">
                      <input v-model="draft.coverageTypes" type="checkbox" :value="option.code" />
                      {{ option.label }}
                    </label>
                  </fieldset>
                  <label>
                    Notes
                    <input v-model="draft.notes" type="text" maxlength="500" />
                  </label>
                  <label>
                    Agency contact email
                    <input v-model="draft.email" type="email" />
                  </label>
                  <label>
                    Phone
                    <input v-model="draft.phone" type="tel" />
                  </label>
                  <p v-if="formError" class="form-error">{{ formError }}</p>
                  <div class="form-actions">
                    <button type="submit" class="btn btn-primary btn-sm" :disabled="saving">Save changes</button>
                    <button type="button" class="btn btn-ghost btn-sm" @click="cancelEdit">Cancel</button>
                  </div>
                </form>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
    <div v-else class="empty-hint">No agents linked yet. Add the broker who writes this entity’s policies.</div>
  </section>
</template>

<script>
import { ref, watch, onMounted } from 'vue';
import api from '@/services/api';
import featureFlags from '@/services/featureFlags';
import {
  fetchCertificateRoute,
  confirmCertificateRoute,
  rejectCertificateRoute,
  clearCertificateRoute,
} from '@/services/agencyCertificateRoute';

const COVERAGE_OPTIONS = [
  { code: 'GL', label: 'General Liability' },
  { code: 'WC', label: 'Workers Compensation' },
  { code: 'AUTO', label: 'Auto Liability' },
  { code: 'UMB', label: 'Umbrella / Excess' },
  { code: 'PL', label: 'Professional Liability' },
  { code: 'EPLI', label: 'Employment Practices' },
  { code: 'CYBER', label: 'Cyber' },
  { code: 'PROP', label: 'Property' },
];

export default {
  name: 'EntityPartiesEditor',
  props: {
    subjectId: { type: [String, Number], required: true },
    agentLinks: { type: Array, default: () => [] },
  },
  emits: ['updated'],
  setup(props, { emit }) {
    const adding = ref(false);
    const editingId = ref(null);
    const saving = ref(false);
    const formError = ref('');
    const searchQuery = ref('');
    const searchResults = ref([]);
    const selectedEntity = ref(null);
    const draft = ref(emptyDraft());
    const routingFeatureOn = ref(false);
    const routeByLinkId = ref({});
    const routeDraftEmail = ref({});
    const routeLoading = ref({});
    const routeSaving = ref({});
    const routeErrors = ref({});
    let searchTimer = null;

    const loadRouteForLink = async (link) => {
      routeLoading.value = { ...routeLoading.value, [link.id]: true };
      routeErrors.value = { ...routeErrors.value, [link.id]: '' };
      try {
        const state = await fetchCertificateRoute(props.subjectId, link.id);
        routeByLinkId.value = { ...routeByLinkId.value, [link.id]: state };
        const suggested = state?.suggested_email || state?.route?.email || '';
        if (suggested && !routeDraftEmail.value[link.id]) {
          routeDraftEmail.value = { ...routeDraftEmail.value, [link.id]: suggested };
        }
      } catch {
        routeByLinkId.value = { ...routeByLinkId.value, [link.id]: null };
      } finally {
        routeLoading.value = { ...routeLoading.value, [link.id]: false };
      }
    };

    const loadAllRoutes = async () => {
      if (!routingFeatureOn.value || !props.agentLinks?.length) return;
      await Promise.all(props.agentLinks.map((link) => loadRouteForLink(link)));
    };

    onMounted(async () => {
      await featureFlags.load();
      routingFeatureOn.value = featureFlags.isEnabled('agency_certificate_request_routing');
      await loadAllRoutes();
    });

    watch(
      () => props.agentLinks,
      () => {
        loadAllRoutes();
      },
      { deep: true },
    );

    function emptyDraft() {
      return { coverageTypes: [], notes: '', email: '', phone: '' };
    }

    const startAdd = () => {
      editingId.value = null;
      adding.value = true;
      formError.value = '';
      selectedEntity.value = null;
      searchQuery.value = '';
      searchResults.value = [];
      draft.value = emptyDraft();
    };

    const cancelAdd = () => {
      adding.value = false;
      formError.value = '';
    };

    const startEdit = (link) => {
      adding.value = false;
      editingId.value = link.id;
      formError.value = '';
      draft.value = {
        coverageTypes: [...(link.coverage_types || [])],
        notes: link.notes || '',
        email: link.agent_entity_email || '',
        phone: link.agent_entity_phone || '',
      };
    };

    const cancelEdit = () => {
      editingId.value = null;
      formError.value = '';
    };

    const onSearchInput = () => {
      if (searchTimer) clearTimeout(searchTimer);
      searchTimer = setTimeout(searchAgencies, 300);
    };

    const searchAgencies = async () => {
      const query = searchQuery.value.trim();
      if (query.length < 2) {
        searchResults.value = [];
        return;
      }
      try {
        const response = await api.get('/entities/', { params: { search: query, limit: 8 } });
        const data = response.data;
        searchResults.value = Array.isArray(data) ? data : (data.results || []);
      } catch {
        searchResults.value = [];
      }
    };

    const selectEntity = (entity) => {
      selectedEntity.value = entity;
      searchQuery.value = entity.name;
      searchResults.value = [];
      draft.value.email = entity.primary_email || draft.value.email;
      draft.value.phone = entity.primary_phone || draft.value.phone;
    };

    const saveLink = async (agentEntityId) => {
      await api.post(`/compliance-subjects/${props.subjectId}/agents/`, {
        agent_entity_id: agentEntityId,
        coverage_types: draft.value.coverageTypes,
        notes: draft.value.notes,
      });
      await api.patch(`/entities/${agentEntityId}/`, {
        primary_email: draft.value.email || '',
        primary_phone: draft.value.phone || '',
      });
    };

    const saveNew = async () => {
      if (!selectedEntity.value) {
        formError.value = 'Search for an agency and select it before saving.';
        return;
      }
      saving.value = true;
      formError.value = '';
      try {
        await saveLink(selectedEntity.value.id);
        adding.value = false;
        emit('updated');
      } catch (err) {
        formError.value = err.response?.data?.error || err.response?.data?.detail || 'Could not save this party.';
      } finally {
        saving.value = false;
      }
    };

    const saveEdit = async (link) => {
      saving.value = true;
      formError.value = '';
      try {
        await saveLink(link.agent_entity_id);
        editingId.value = null;
        emit('updated');
      } catch (err) {
        formError.value = err.response?.data?.error || err.response?.data?.detail || 'Could not save this party.';
      } finally {
        saving.value = false;
      }
    };

    const confirmRoute = async (link) => {
      const email = (routeDraftEmail.value[link.id] || '').trim();
      if (!email) {
        routeErrors.value = { ...routeErrors.value, [link.id]: 'Enter the certificate desk email.' };
        return;
      }
      routeSaving.value = { ...routeSaving.value, [link.id]: true };
      routeErrors.value = { ...routeErrors.value, [link.id]: '' };
      try {
        await confirmCertificateRoute(props.subjectId, link.id, { email });
        await loadRouteForLink(link);
        emit('updated');
      } catch (err) {
        routeErrors.value = {
          ...routeErrors.value,
          [link.id]: err.response?.data?.error || 'Could not confirm certificate destination.',
        };
      } finally {
        routeSaving.value = { ...routeSaving.value, [link.id]: false };
      }
    };

    const rejectSuggestion = async (link) => {
      const email = (routeDraftEmail.value[link.id] || routeByLinkId.value[link.id]?.suggested_email || '').trim();
      if (!email) return;
      routeSaving.value = { ...routeSaving.value, [link.id]: true };
      try {
        await rejectCertificateRoute(props.subjectId, link.id, { email });
        await loadRouteForLink(link);
      } catch (err) {
        routeErrors.value = {
          ...routeErrors.value,
          [link.id]: err.response?.data?.error || 'Could not save rejection.',
        };
      } finally {
        routeSaving.value = { ...routeSaving.value, [link.id]: false };
      }
    };

    const clearRoute = async (link) => {
      routeSaving.value = { ...routeSaving.value, [link.id]: true };
      try {
        await clearCertificateRoute(props.subjectId, link.id);
        routeDraftEmail.value = { ...routeDraftEmail.value, [link.id]: '' };
        await loadRouteForLink(link);
      } catch (err) {
        routeErrors.value = {
          ...routeErrors.value,
          [link.id]: err.response?.data?.error || 'Could not clear route.',
        };
      } finally {
        routeSaving.value = { ...routeSaving.value, [link.id]: false };
      }
    };

    const removeLink = async (link) => {
      if (!window.confirm(`Remove ${link.agent_entity_name} from this entity?`)) return;
      formError.value = '';
      try {
        await api.delete(`/compliance-subjects/${props.subjectId}/agents/${link.id}/`);
        if (editingId.value === link.id) editingId.value = null;
        emit('updated');
      } catch (err) {
        formError.value = err.response?.data?.error || 'Could not remove this party.';
      }
    };

    return {
      coverageOptions: COVERAGE_OPTIONS,
      adding,
      editingId,
      saving,
      formError,
      searchQuery,
      searchResults,
      selectedEntity,
      draft,
      startAdd,
      cancelAdd,
      startEdit,
      cancelEdit,
      onSearchInput,
      selectEntity,
      saveNew,
      saveEdit,
      removeLink,
      routingFeatureOn,
      routeByLinkId,
      routeDraftEmail,
      routeLoading,
      routeSaving,
      routeErrors,
      confirmRoute,
      rejectSuggestion,
      clearRoute,
    };
  },
};
</script>

<style scoped>
.section-heading-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.section-hint {
  color: #666;
  font-size: 0.85rem;
  margin: 0 0 1rem;
}

.parties-table-wrap {
  overflow-x: auto;
}

.parties-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.parties-table th,
.parties-table td {
  text-align: left;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #eee;
  vertical-align: top;
}

.parties-table th {
  font-weight: 600;
  color: #555;
}

.party-note,
.muted {
  display: block;
  color: #777;
  font-size: 0.8rem;
}

.coverage-type-tag {
  display: inline-block;
  margin: 0 0.35rem 0.25rem 0;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 0.75rem;
}

.row-actions {
  white-space: nowrap;
}

.btn-link {
  border: 0;
  background: transparent;
  color: #4f46e5;
  cursor: pointer;
  font-weight: 600;
  margin-right: 0.75rem;
  padding: 0;
}

.btn-link.danger {
  color: #b91c1c;
}

.party-form {
  display: grid;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding: 0.9rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
}

.party-form label {
  display: grid;
  gap: 0.25rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #334155;
}

.party-form input[type='text'],
.party-form input[type='search'],
.party-form input[type='email'],
.party-form input[type='tel'] {
  font-weight: 400;
  padding: 0.4rem 0.55rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
}

fieldset {
  border: 0;
  margin: 0;
  padding: 0;
}

legend {
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
}

.check-option {
  display: inline-flex !important;
  align-items: center;
  gap: 0.35rem;
  margin: 0 0.8rem 0.35rem 0;
  font-weight: 500 !important;
}

.search-results {
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
}

.search-results button {
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  padding: 0.45rem 0.6rem;
  cursor: pointer;
}

.search-results button:hover {
  background: #eef2ff;
}

.selected-entity,
.form-error {
  margin: 0;
  font-size: 0.85rem;
}

.form-error {
  color: #b91c1c;
}

.form-actions {
  display: flex;
  gap: 0.5rem;
}

.empty-hint {
  color: #666;
  padding: 0.5rem 0 1rem;
}

.contact-kind {
  display: block;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #888;
  margin-bottom: 0.15rem;
}

.cert-route-row td {
  background: #fafafa;
  border-bottom: 1px solid #eee;
}

.cert-route-panel {
  padding: 0.35rem 0 0.5rem;
}

.cert-route-heading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
}

.cert-route-copy {
  margin: 0 0 0.5rem;
  font-size: 0.85rem;
  color: #444;
}

.cert-route-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.cert-route-input {
  min-width: 220px;
  flex: 1;
  max-width: 320px;
  padding: 0.35rem 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.danger-text {
  color: #b91c1c;
}
</style>
